import { useCallback, useEffect, useRef } from 'react';
import { request as baseRequest } from '../utils/http-common.js';
import {
  abortRequest,
  abortRequestsByPrefix,
  isRequestCancelled,
} from '../utils/requestCancellation.js';

export function useCancellableRequest(scopePrefix = '') {
  const scopeRef = useRef(scopePrefix);
  scopeRef.current = scopePrefix;

  const buildCancelKey = useCallback((url, method, cancelKey) => {
    const key = cancelKey ?? `${method}:${url}`;
    return scopeRef.current ? `${scopeRef.current}:${key}` : key;
  }, []);

  const cancellableRequest = useCallback(
    async (url, method, data = {}, options = {}) => {
      const { cancelKey: explicitKey, ...rest } = options;
      const fullCancelKey = buildCancelKey(url, method, explicitKey);
      return baseRequest(url, method, data, {
        ...rest,
        cancelKey: fullCancelKey,
      });
    },
    [buildCancelKey]
  );

  const abort = useCallback((cancelKey) => {
    if (!cancelKey) return;
    const fullKey = scopeRef.current
      ? `${scopeRef.current}:${cancelKey}`
      : cancelKey;
    abortRequest(fullKey);
  }, []);

  const abortAll = useCallback(() => {
    if (scopeRef.current) {
      abortRequestsByPrefix(`${scopeRef.current}:`);
    }
  }, []);

  useEffect(() => () => {
    if (scopeRef.current) {
      abortRequestsByPrefix(`${scopeRef.current}:`);
    }
  }, []);

  return {
    request: cancellableRequest,
    abort,
    abortAll,
    isRequestCancelled,
  };
}

export default useCancellableRequest;
